import { useEffect, useMemo, useState } from 'react';
import {
  Modal, Form, Select, Input, Button, Space, Tag, RadioGroup, Row, Col, Alert, message, Tooltip, StatusBadge, EntityAvatar, CodeChip,
} from '../../ui';
import { Paperclip, X, Camera } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useAuthStore, usePermissionChecker } from '../../store/auth';
import { formatIstDate } from '../../lib/time';
import { productImage } from '../../lib/imageUrls';
import { inr } from '../../lib/format';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { SEVERITY_LABEL, SEVERITY_TONE } from './complaintWorkflow';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRecordSearch';
import { variantText, isRedundantSku } from '../../lib/format';

const SEVERITIES = (['LOW', 'MED', 'HIGH', 'CRIT'] as const).map((v) => ({ value: v, label: SEVERITY_LABEL[v] }));
const SEV_RANK: Record<string, number> = { LOW: 0, MED: 1, HIGH: 2, CRIT: 3 };

/**
 * PRODUCT AND VARIANT ARE TWO QUESTIONS, SO THEY ARE TWO DROPDOWNS.
 *
 * A "product" in this catalogue is one row per PACK — AGN 250 ml and AGN 1 L are separate rows sharing
 * a `group_code`. Offering those rows flat asked the raiser to find their pack inside a list of every
 * pack we sell, and the option read "AGN · AGN 500ml": the label was `name · sku`, and the SKU is the
 * name with the pack stapled on, so the product's name was printed twice before the size was reached.
 *
 * Grouping by `group_code` (falling back to the name for the legacy rows that have none) turns it into
 * the question a dealer actually answers: WHICH PRODUCT, then WHICH PACK. The variant list is short and
 * says only what differs — "250 ml", "500 ml", "1 L" — and the SKU is appended only when it is not just
 * a re-spelling of the two, which is the one case where it carries information ("500 ml · AGN-1L").
 *
 * Same shape as the transfer form's product → variant → batch cascade, deliberately: it is the same
 * question, and a picker that behaves differently in two places is two things to learn.
 */
interface ProductFamily { key: string; name: string; variants: any[] }

/** Sorts packs small → large so a family reads "250 ml, 500 ml, 1 L" rather than in insertion order. */
const byPack = (a: any, b: any) => (Number(a.packSize) || 0) - (Number(b.packSize) || 0);

/** One variant's label: the pack, plus the SKU only when the SKU says something the pack doesn't. */
function variantOption(p: any): { value: string; label: string } {
  const pack = variantText(p.packSize, p.uomCode, p.variantLabel) || String(p.variantLabel ?? p.sku ?? '');
  const skuAdds = p.sku && !isRedundantSku(p.sku, p.name, pack, `${p.name} ${pack}`, `${p.name} ${p.variantLabel ?? ''}`);
  return { value: String(p.id), label: skuAdds ? `${pack} · ${p.sku}` : pack };
}

/**
 * The two fields, as a pair of columns. Used twice — for what was billed and, on a mismatch category,
 * for what actually arrived — so the two sides of a wrong-product claim are picked the same way.
 *
 * A family with one pack selects it on the way through: the second dropdown then confirms rather than
 * asks, and nobody is made to answer a question with only one answer.
 */
function ProductVariantFields({
  families, familyKey, productId, onChange, label, required, span = 8,
}: {
  families: ProductFamily[];
  familyKey: string;
  productId: string;
  onChange: (familyKey: string, productId: string) => void;
  label: string;
  required?: boolean;
  span?: number;
}) {
  const variants = families.find((f) => f.key === familyKey)?.variants ?? [];
  return (
    <>
      <Col xs={24} sm={span}>
        <Form.Item label={label} required={required}>
          <Select
            allowClear
            showSearch
            optionFilterProp="searchLabel"
            placeholder="Pick a product"
            value={familyKey || undefined}
            onChange={(v) => {
              const k = v ? String(v) : '';
              const fam = families.find((f) => f.key === k);
              // One pack → pick it here; more than one → the variant field is the next question.
              onChange(k, fam && fam.variants.length === 1 ? String(fam.variants[0].id) : '');
            }}
            /*
              * Each product carries its IDENTITY MARK — the house `EntityAvatar`, whose hue is derived
              * from the name, so a product is the same colour in this picker as it is on the stock
              * page and the transfer dialog. A list of twenty near-identical agri-input names
              * ("AGN", "AGN LTE", "AminOPF") is close to unreadable as plain text; the mark is what
              * the eye actually finds the row by.
              */
            options={families.map((f) => ({
              value: f.key,
              searchLabel: f.name,
              label: (
                <span className="flex min-w-0 items-center gap-2">
                  {/*
                    * The PRODUCT'S OWN PHOTO where the catalogue has one — a dealer recognises the
                    * bottle long before they recognise "AGN LTE". Falls back to the name-derived
                    * initials mark, which still varies row to row; a single repeated package glyph
                    * would be decoration rather than identity (house rule).
                    */}
                  <EntityAvatar name={f.name} src={productImage(f.variants[0])} square size={26} />
                  <span className="truncate text-content">{f.name}</span>
                  {f.variants.length > 1
                    ? <span className="ml-auto shrink-0 type-caption text-subtle">{f.variants.length} packs</span>
                    : null}
                </span>
              ),
            }))}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={span}>
        <Form.Item label="Variant" required={required}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!familyKey}
            placeholder={familyKey ? 'Which pack?' : 'Pick a product first'}
            notFoundContent="No packs on this product"
            value={productId || undefined}
            onChange={(v) => onChange(familyKey, v ? String(v) : '')}
            options={variants.map(variantOption)}
          />
        </Form.Item>
      </Col>
    </>
  );
}

/**
 * Raise a complaint.
 *
 * The categories are no longer a hard-coded list in this file — they come from the category master,
 * and each one carries its OWN rules: whether evidence is mandatory and how much, whether the product
 * and batch have to be named, its default urgency and its SLA.
 *
 * This form PREVIEWS those rules; it does not enforce them. The server re-checks every one on POST,
 * because a rule that only exists in React is bypassed by the mobile app, by an integration, or by
 * anyone with a token — which are exactly the routes a thin claim arrives on. What the form is for is
 * making sure nobody finds out about the requirement by being rejected: the moment a category is
 * picked, the form says which photo is needed and the submit button stays disabled until it is there.
 */
export function ComplaintFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [attachments, setAttachments] = useState<string[]>([]);
  const [partyType, setPartyType] = useState<'RETAILER' | 'FARMER'>('RETAILER');
  const [categoryId, setCategoryId] = useState<string>('');
  const [severity, setSeverity] = useState<string>('MED');
  // The product is picked in two steps — family, then pack — so both halves are state. Only
  // `productId` (the variant, which is the catalogue row) is ever sent.
  const [productFamily, setProductFamily] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  /*
   * The batch is a PICKER once a product is chosen, with a deliberate escape hatch. `OTHER` reveals
   * a free-text box, and a code that matches nothing we manufactured is the single most valuable
   * answer this form can collect — a closed list would make it unrecordable.
   */
  const [batchNo, setBatchNo] = useState<string>('');
  const [batchOther, setBatchOther] = useState<string>('');
  // The mismatch side, shown only for categories the master marks as `capturesReceivedItem`.
  const [receivedFamily, setReceivedFamily] = useState<string>('');
  const [receivedProductId, setReceivedProductId] = useState<string>('');
  const [receivedBatchNo, setReceivedBatchNo] = useState<string>('');
  const [documentRef, setDocumentRef] = useState<string>('');
  /** The consignment this is about, when the complainant can point at one. */
  const [orderId, setOrderId] = useState<string>('');

  // Staff (admin / super admin / sales head / sales person) raise complaints ON BEHALF OF a retailer
  // or farmer; a customer login is auto-scoped to itself by the backend, so they don't pick a party.
  const isStaff = !useAuthStore((s) => s.isCustomer)();
  const hasPerm = usePermissionChecker();

  const { data: categories } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: () => request<any[]>({ url: '/complaint-categories' }),
    enabled: open,
  });
  /*
   * Whose orders to offer.
   *
   * A staff user is raising ON BEHALF of the dealer they picked; a dealer raising their own is scoped
   * server-side and reads their own id off the session. A farmer has no orders of their own — they
   * buy through a dealer — so the field is simply not shown to them.
   */
  const partyId = Form.useWatch('partyId', form);
  // Both parties are searched on the server — a first page of 200 hid everyone past it.
  const retailerPick = useRetailerSearch({
    key: 'complaint-retailers',
    enabled: open && isStaff && hasPerm('RETAILER.VIEW'),
    selected: partyType === 'RETAILER' ? partyId : undefined,
  });
  const farmerPick = useFarmerSearch({
    key: 'complaint-farmers',
    enabled: open && isStaff && hasPerm('FARMER.VIEW'),
    selected: partyType === 'FARMER' ? partyId : undefined,
  });
  const myRole = useAuthStore((st) => st.user?.role);
  /*
   * A dealer raising their own complaint needs no id here: `GET /orders` is scoped server-side to
   * the caller's own retailer for a RETAILER login, so asking for "the orders" returns theirs and
   * theirs only. Staff must name the dealer first — before that there is no list to show.
   */
  const forOwnOrders = !isStaff && myRole === 'RETAILER';
  const complainantRetailerId = isStaff ? (partyType === 'RETAILER' ? partyId : null) : null;
  const showOrderField = forOwnOrders || !!complainantRetailerId;

  const { data: dealerOrders } = useQuery({
    queryKey: ['complaint-orders', complainantRetailerId ?? 'mine'],
    queryFn: () => request<any>({
      url: '/orders',
      // `includeClosed`: the list hides CLOSED orders by default, which dropped exactly the finished deliveries a
      // complaint is usually about — a dealer saw a cancelled and two unshipped orders, not the one delivered (E2E 15 Sep).
      params: { ...(complainantRetailerId ? { retailerId: complainantRetailerId } : {}), pageSize: 50, sort: '-orderDate', includeClosed: 'true' },
    }),
    enabled: open && showOrderField,
  });
  /*
   * An order as a ROW, not a dot-joined sentence.
   *
   * "GB/2026/09/00166 · 05 Sep 2026 · ₹42,480" is three facts of different kinds run together with
   * separators, and the eye has to parse the punctuation to find any of them. Laid out, the document
   * number leads in mono (it is a code), the value sits right-aligned where money belongs, and the
   * second line carries the date, where the order got to, and what was actually in it — which is how
   * a dealer recognises "the delivery I am complaining about".
   */
  const orderOptions = useMemo(() => {
    const rows = dealerOrders?.data ?? dealerOrders ?? [];
    return (Array.isArray(rows) ? rows : []).map((o: any) => {
      const items: any[] = Array.isArray(o.products) ? o.products : [];
      const what = items.slice(0, 2).map((i) => i.sku || i.name).filter(Boolean).join(', ');
      const more = items.length > 2 ? ` +${items.length - 2} more` : '';
      return {
        value: String(o.id),
        searchLabel: `${o.orderNo} ${o.orderDate ?? ''} ${what}`,
        label: (
          <span className="flex min-w-0 flex-col gap-0.5 py-0.5">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate font-mono text-xs text-content">{o.orderNo}</span>
              <StatusBadge status={o.status} />
              {o.grandTotal ? <span className="ml-auto shrink-0 font-semibold tabular-nums">{inr(o.grandTotal)}</span> : null}
            </span>
            <span className="truncate type-caption text-subtle">
              {o.orderDate ? formatIstDate(o.orderDate) : '—'}
              {what ? ` · ${what}${more}` : ''}
            </span>
          </span>
        ),
      };
    });
  }, [dealerOrders]);

  /*
   * The category, with the urgency it opens at — and nothing else.
   *
   * An earlier pass also printed the SLA hours and an evidence paperclip on every row. It was true
   * but it was noise: eight rows each repeating two pieces of small print, when both facts are
   * already said ONCE, in full sentences, the moment a category is chosen — the description line
   * ("We aim to respond within 24 hours") and the evidence alert. A picker should help you choose,
   * not brief you.
   */
  const categoryOptions = useMemo(() => (categories ?? []).map((c: any) => ({
    value: String(c.id),
    searchLabel: `${c.name} ${c.code ?? ''}`,
    label: (
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-content">{c.name}</span>
        {c.defaultSeverity ? <Tag tone={SEVERITY_TONE[c.defaultSeverity]}>{SEVERITY_LABEL[c.defaultSeverity] ?? c.defaultSeverity}</Tag> : null}
      </span>
    ),
  })), [categories]);

  const { data: products } = useQuery({
    // Active products only — archived/deactivated SKUs must not be selectable as a complaint subject.
    queryKey: ['complaint-products', 'active'],
    queryFn: () => requestAllPages('/products', { active: 'true' }),
    enabled: open,
  });

  /*
   * Batches for the chosen product, from the inventory master. A product has many batches and a
   * dealer is transcribing a code off a crimped foil seal — as free text, "GB2451", "GB-2451" and
   * "G82451" all became different batches that no analytics could ever group.
   */
  const { data: batches } = useQuery({
    queryKey: ['complaint-product-batches', productId],
    queryFn: () => request<any[]>({ url: '/complaints/product-batches', params: { productId } }),
    enabled: open && !!productId,
  });

  const rule = useMemo(
    () => (categories ?? []).find((c: any) => String(c.id) === String(categoryId)) ?? null,
    [categories, categoryId],
  );

  const batchOptions = useMemo(() => [
    ...(batches ?? []).map((b: any) => ({
      value: b.batchNo,
      searchLabel: b.batchNo,
      label: (
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <CodeChip className="shrink-0">{b.batchNo}</CodeChip>
          {b.expDate ? <span className="type-caption text-muted">exp {String(b.expDate).slice(0, 10)}</span> : null}
        </span>
      ),
    })),
    { value: 'OTHER', label: 'Not in this list — type it in' },
  ], [batches]);

  /** What actually gets sent: the picked batch, or whatever was typed under "Not in this list". */
  const effectiveBatch = batchNo === 'OTHER' ? batchOther.trim() : batchNo;

  /*
   * Picking a category adopts its default urgency, because the category is the better-informed
   * guess: a leaking pack is a 24-hour problem whoever files it. The raiser can still raise the
   * severity — the server keeps the category's default as a FLOOR and rejects anything below it,
   * so nothing can be filed as Low to dodge a shorter SLA.
   */
  // Changing the product invalidates the batch beneath it — leaving a stale batch attached to a
  // different product is how a complaint ends up citing a batch that product never had.
  useEffect(() => { setBatchNo(''); setBatchOther(''); }, [productId]);

  useEffect(() => {
    if (!rule) return;
    setSeverity((cur) => (SEV_RANK[cur] > SEV_RANK[rule.defaultSeverity] ? cur : rule.defaultSeverity));
    form.setFieldValue('severity', SEV_RANK[severity] > SEV_RANK[rule.defaultSeverity] ? severity : rule.defaultSeverity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.id]);

  /*
   * The catalogue rows folded into families: siblings share `group_code`, and a row without one (the
   * older imports and the test fixtures) stands alone under its own name.
   */
  const families = useMemo<ProductFamily[]>(() => {
    const m = new Map<string, ProductFamily>();
    for (const p of (products?.data ?? []) as any[]) {
      const g = String(p.groupCode ?? '').trim();
      const key = g ? `g:${g}` : `n:${p.name}`;
      const e: ProductFamily = m.get(key) ?? { key, name: p.name, variants: [] };
      e.variants.push(p);
      m.set(key, e);
    }
    return [...m.values()]
      .map((f) => ({ ...f, variants: [...f.variants].sort(byPack) }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [products]);

  const needed = rule?.requiresAttachment ? Math.max(1, rule.minAttachments || 1) : (rule?.minAttachments ?? 0);
  const shortBy = Math.max(0, needed - attachments.length);

  /*
   * Mirror of the server's mismatch rule, so the raiser is told before submitting rather than by a
   * rejection. The server re-checks: this is a courtesy, not the enforcement.
   */
  const mismatchError = useMemo(() => {
    if (!rule?.capturesReceivedItem) return null;
    const gotProduct = !!receivedProductId;
    const gotBatch = !!receivedBatchNo.trim();
    if (!gotProduct && !gotBatch) return 'Tell us what you received — the product, the batch code, or both.';
    const productDiffers = gotProduct && receivedProductId !== productId;
    const batchDiffers = gotBatch && receivedBatchNo.trim().toUpperCase() !== effectiveBatch.trim().toUpperCase();
    if (!productDiffers && !batchDiffers) {
      return 'What you received matches what you were billed. If something else is wrong, pick a different category.';
    }
    return null;
  }, [rule, receivedProductId, receivedBatchNo, productId, effectiveBatch]);

  const create = useConfirmedMutation({ title: "Raise this complaint?", content: "It is logged and routed to whoever handles this category.", okText: "Yes, raise it" }, {
    mutationFn: (vals: any) => request<any>({
      url: '/complaints',
      method: 'POST',
      data: {
        categoryId,
        severity: vals.severity,
        description: vals.description,
        productId: productId || undefined,
        batchNo: effectiveBatch || undefined,
        ...(rule?.capturesReceivedItem ? {
          receivedProductId: receivedProductId || undefined,
          receivedBatchNo: receivedBatchNo.trim() || undefined,
          documentRef: documentRef.trim() || undefined,
        } : {}),
        attachments: attachments.length ? attachments : undefined,
        source: 'WEB',
        // On-behalf-of: only staff send a party; customers are scoped server-side.
        ...(isStaff && partyType === 'RETAILER' ? { retailerId: vals.partyId } : {}),
        ...(isStaff && partyType === 'FARMER' ? { farmerId: vals.partyId } : {}),
        orderId: orderId || undefined,
      },
    }),
    onSuccess: (c: any) => {
      message.success(`Complaint ${c?.complaintNo ?? ''} raised`);
      qc.invalidateQueries({ queryKey: ['complaints'] });
      qc.invalidateQueries({ queryKey: ['complaint-summary'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      close();
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not raise complaint'),
  });

  const close = () => {
    form.resetFields();
    setAttachments([]);
    setCategoryId('');
    setSeverity('MED');
    setProductFamily('');
    setProductId('');
    setBatchNo('');
    setBatchOther('');
    setReceivedFamily('');
    setReceivedProductId('');
    setReceivedBatchNo('');
    setDocumentRef('');
    onClose();
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) close(); }} title="Raise a Complaint" width={720}>
      <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)} initialValues={{ severity: 'MED' }}>
        {isStaff && (
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item label="Raise on behalf of">
                <RadioGroup
                  inline
                  value={partyType}
                  onChange={(v) => { setPartyType(v as 'RETAILER' | 'FARMER'); form.setFieldValue('partyId', undefined); }}
                  options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item
                name="partyId"
                label={partyType === 'RETAILER' ? 'Retailer' : 'Farmer'}
                rules={[{ required: true, message: `Select the ${partyType === 'RETAILER' ? 'retailer' : 'farmer'} this is for` }]}
              >
                <Select
                  {...CUSTOMER_SELECT_PROPS}
                  placeholder={partyType === 'RETAILER' ? 'Search retailers…' : 'Search farmers…'}
                  filterOption={false} onSearch={partyType === 'RETAILER' ? retailerPick.onSearch : farmerPick.onSearch}
                  options={
                    partyType === 'RETAILER'
                      ? retailerPick.rows.map((r: any) => customerOption(r, 'RETAILER'))
                      : farmerPick.rows.map((f: any) => customerOption(f, 'FARMER'))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/*
          * WHICH ORDER, optional.
          *
          * Most complaints are about a consignment, and naming it here saves the handler the one
          * question they always have to go and answer: what was billed, and what else came with it.
          * Only offered once the dealer is known — an order list is meaningless before that, and a
          * farmer buys through a dealer so has no order of their own to point at.
          */}
        {showOrderField && (
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item label="Related order (optional)">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="searchLabel"
                  value={orderId || undefined}
                  onChange={(v) => setOrderId(v ? String(v) : '')}
                  placeholder={orderOptions.length ? 'Which delivery is this about?' : 'No orders for this dealer yet'}
                  disabled={!orderOptions.length}
                  options={orderOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Category" required>
              <Select
                value={categoryId || undefined}
                onChange={(v) => setCategoryId(String(v))}
                placeholder="What is this about?"
                showSearch
                optionFilterProp="searchLabel"
                options={categoryOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="severity" label="How urgent is it?" rules={[{ required: true }]}>
              <Select
                value={severity}
                onChange={(v) => setSeverity(String(v))}
                options={SEVERITIES}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* What this category means + how long we have to answer it. */}
        {rule?.description && (
          <div className="-mt-2 mb-3 text-xs text-muted">
            {rule.description}
            {rule.slaHours ? ` We aim to respond within ${rule.slaHours} hours.` : ''}
          </div>
        )}

        <Form.Item name="description" label="Describe the issue" rules={[{ required: true, message: 'Please describe the issue' }]}>
          <Input.TextArea rows={4} placeholder="Tell us what happened…" />
        </Form.Item>

        <Row gutter={16}>
          <ProductVariantFields
            families={families}
            familyKey={productFamily}
            productId={productId}
            onChange={(k, id) => { setProductFamily(k); setProductId(id); }}
            label={rule?.requiresProduct ? 'Related product' : 'Related product (optional)'}
            required={!!rule?.requiresProduct}
          />
          <Col xs={24} sm={8}>
            {/* The batch picker only appears once a variant is chosen — batches belong to one pack,
                and offering the field before then invites a code typed against the wrong SKU. */}
            <Form.Item label={rule?.requiresBatch ? 'Batch number' : 'Batch number (optional)'} required={!!rule?.requiresBatch}>
              {!productId ? (
                <Input disabled placeholder="Pick a variant first" />
              ) : (
                <Select
                  showSearch
                  optionFilterProp="searchLabel"
                  placeholder={batches?.length ? 'The batch on the pack' : 'None on record — pick "Not in this list"'}
                  value={batchNo || undefined}
                  onChange={(v) => setBatchNo(String(v))}
                  options={batchOptions}
                />
              )}
            </Form.Item>
            {batchNo === 'OTHER' && (
              <Input
                className="-mt-2"
                value={batchOther}
                onChange={(e) => setBatchOther(e.target.value)}
                placeholder="Type the batch code exactly as printed"
              />
            )}
          </Col>
        </Row>

        {/*
          * THE MISMATCH BLOCK — only for categories the master marks as capturing what was received
          * (Wrong Product, Wrong Batch Received, and anything an admin ticks later).
          *
          * The fields above describe OUR document; these describe what physically turned up. Both
          * sides are needed or the complaint cannot be investigated: the warehouse has nothing to
          * check the picking list against.
          */}
        {/* `!!` because these master flags arrive as tinyint 0/1, and `0 && …` RENDERS the 0 — a
            bare "0" was printing above the attachments label. (See the house gotcha note.) */}
        {!!rule?.capturesReceivedItem && (
          <div className="mb-4 rounded-card border border-line bg-surface-2 p-3">
            <div className="type-body-strong mb-1">What actually arrived</div>
            <p className="type-caption mb-3 text-muted">
              The fields above are what your invoice, proforma or our website says. Tell us what was
              really in the carton — a batch code we have no record of is exactly what we need to know.
            </p>
            <Row gutter={16}>
              <ProductVariantFields
                families={families}
                familyKey={receivedFamily}
                productId={receivedProductId}
                onChange={(k, id) => { setReceivedFamily(k); setReceivedProductId(id); }}
                label="Product received (if different)"
              />
              <Col xs={24} sm={8}>
                <Form.Item label="Batch code received">
                  <Input
                    value={receivedBatchNo}
                    onChange={(e) => setReceivedBatchNo(e.target.value)}
                    placeholder="Exactly as printed"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Invoice / proforma number (optional)">
              <Input
                value={documentRef}
                onChange={(e) => setDocumentRef(e.target.value)}
                placeholder="e.g. INV/00902 — or 'WEBSITE' if you ordered online"
              />
            </Form.Item>
            {mismatchError && <Alert type="warning" description={mismatchError} />}
          </div>
        )}

        <Form.Item
          /* Says what to DO, not what the record requires: "1 photo required" reads as a rule the
             reader has to satisfy from nothing, while "Add 1 photo" is the instruction itself. The
             count that remains outstanding lives in the alert underneath, which is the thing that
             changes as they attach. */
          label={needed > 0
            ? `Photos / attachments — ${needed === 1 ? 'at least 1 needed' : `at least ${needed} needed`}`
            : 'Photos / attachments (optional)'}
        >
          {/*
            * The evidence rule, stated BEFORE they try to submit and quoting the category's own hint,
            * so "attach a photo" says which photo. A raiser who finds out what was needed only by
            * being rejected has already lost the pack, thrown away the carton, or given up.
            */}
          {needed > 0 && (
            <Alert
              className="mb-2"
              type={shortBy > 0 ? 'warning' : 'success'}
              title={shortBy > 0
                ? `${shortBy} more attachment${shortBy === 1 ? '' : 's'} needed`
                : 'Evidence attached'}
              description={rule?.attachmentHint ?? 'Attach a clear photo of the problem.'}
            />
          )}
          {/*
            * The house MULTI-file field: pick several photos in one go, each shown as a thumbnail
            * with its own remove button. It was a single-file picker appending one URL at a time, so
            * a dealer photographing a damaged carton from three angles had to open the file dialog
            * three times — and the evidence rule for most categories asks for more than one shot.
            */}
          <MultiFileUploadField
            category="attachment"
            buttonLabel={needed > 0 ? 'Add photos / documents' : 'Attach files'}
            value={attachments}
            onChange={setAttachments}
          />
        </Form.Item>

        <Button
          variant="primary"
          type="submit"
          block
          loading={create.isPending}
          disabled={
            !categoryId || shortBy > 0 || !!mismatchError
            || (!!rule?.requiresProduct && !productId)
            || (!!rule?.requiresBatch && !effectiveBatch)
          }
          icon={shortBy > 0 ? <Camera size={16} /> : undefined}
        >
          {!categoryId
            ? 'Pick a category to continue'
            : rule?.requiresProduct && !productId
              ? (productFamily ? 'Pick which pack it was' : 'Pick the product this is about')
              : rule?.requiresBatch && !effectiveBatch
                ? 'Enter the batch number'
                : mismatchError
                  ? 'Tell us what you actually received'
                  : shortBy > 0
                    ? `Attach ${shortBy} more file${shortBy === 1 ? '' : 's'} to submit`
                    : 'Submit complaint'}
        </Button>
      </Form>
    </Modal>
  );
}
